import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import axios from "axios";
import { useTemplate } from "@/context/TemplateContext";
import SafeImage from "../../components/SafeImage";
import ProductCard from "./components/ProductCard";

/**
 * Iter 77 — SRC-architecture ecommerce sub-pages (About / Contact / FAQ /
 * Best Sellers / policy documents). These replace the legacy
 * pages[].sections[] rendering for the standard slugs, in BOTH the public
 * site and the Visual Editor, so what you edit is exactly what visitors see.
 *
 * Every visible element carries a data-testid wired to an editable anchor:
 *  • headings/eyebrows → copy_overrides `pages.*`
 *  • about body        → canonical `website_content.about`
 *  • about image       → canonical `about_image_1`
 *  • contact info      → canonical `contact_email/phone/address`
 *  • contact form copy → copy_overrides `forms.contact.*` (submits to the
 *                        real /public/site/{slug}/contact endpoint)
 *  • FAQ entries       → canonical `faqs` list (FaqEditor)
 *  • policy bodies     → canonical `policy_<slug>` scalars
 */

const API = process.env.REACT_APP_BACKEND_URL;

function PageShell({ eyebrow, eyebrowTestId, title, titleTestId, children }) {
  return (
    <section className="pt-28 sm:pt-32 pb-24 px-4 sm:px-8 max-w-7xl mx-auto w-full">
      <div className="mb-12">
        <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500" data-testid={eyebrowTestId}>{eyebrow}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3 leading-none" data-testid={titleTestId}>{title}</h1>
      </div>
      {children}
    </section>
  );
}

export function EcomAbout() {
  const { config: cfg } = useTemplate();
  const pc = cfg.copy?.pages?.about || {};
  const body = cfg.aboutBody || "We're a small team obsessed with getting the details right. Every product we make is designed to be used daily and loved for years.";
  const paras = body.split(/\n+/).filter(Boolean);
  return (
    <PageShell eyebrow={pc.eyebrow || "— About"} eyebrowTestId="about-page-eyebrow" title={pc.title || "About us."} titleTestId="about-page-title">
      <div className="grid lg:grid-cols-2 gap-14 items-start">
        <div className="space-y-5 text-neutral-600 leading-relaxed text-lg" data-testid="about-page-body">
          {paras.map((p, i) => <p key={i}>{p}</p>)}
        </div>
        {cfg.aboutImage ? (
          <div className="aspect-[4/3] overflow-hidden" data-testid="about-image-1">
            <SafeImage src={cfg.aboutImage} alt={pc.title || "About"} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[4/3] bg-neutral-100 grid place-items-center text-neutral-400 font-display text-6xl" data-testid="about-image-1">
            {(cfg.brand?.name || "S").charAt(0)}
          </div>
        )}
      </div>
    </PageShell>
  );
}

export function EcomContact() {
  const { config: cfg, slug } = useTemplate();
  const pc = cfg.copy?.pages?.contact || {};
  const fc = cfg.copy?.forms?.contact || {};
  const info = cfg.contactInfo || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/public/site/${slug}/contact`, { ...form, source: "contact" });
      toast.success(fc.successTitle || "Message received!", { description: fc.successBody || "We'll get back to you shortly." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full border border-neutral-300 bg-white px-4 py-3 text-sm outline-none focus:border-black";
  return (
    <PageShell eyebrow={pc.eyebrow || "— Contact"} eyebrowTestId="contact-page-eyebrow" title={pc.title || "Get in touch."} titleTestId="contact-page-title">
      <div className="grid lg:grid-cols-2 gap-14">
        <div className="space-y-6 text-sm">
          {info.email && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Email</p>
              <p className="text-lg" data-testid="contact-info-email">{info.email}</p>
            </div>
          )}
          {info.phone && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Phone</p>
              <p className="text-lg" data-testid="contact-info-phone">{info.phone}</p>
            </div>
          )}
          {info.address && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-1">Address</p>
              <p className="text-lg" data-testid="contact-info-address">{info.address}</p>
            </div>
          )}
        </div>
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="contact-form">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 block mb-1.5">{fc.nameLabel || "Full Name *"}</label>
            <input className={inputCls} value={form.name} onChange={set("name")} placeholder={fc.namePlaceholder || "Jane Doe"} data-testid="contact-name" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 block mb-1.5">{fc.emailLabel || "Email *"}</label>
            <input className={inputCls} type="email" value={form.email} onChange={set("email")} placeholder={fc.emailPlaceholder || "jane@email.com"} data-testid="contact-email" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 block mb-1.5">{fc.phoneLabel || "Phone"}</label>
            <input className={inputCls} type="tel" value={form.phone} onChange={set("phone")} placeholder={fc.phonePlaceholder || "(555) 000-1234"} data-testid="contact-phone" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 block mb-1.5">{fc.messageLabel || "How can we help? *"}</label>
            <textarea className={`${inputCls} min-h-[120px]`} value={form.message} onChange={set("message")} placeholder={fc.messagePlaceholder || "Tell us what you need…"} data-testid="contact-message" />
          </div>
          <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-black text-white px-8 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-60" data-testid="contact-submit">
            {sending ? "Sending…" : (fc.submitLabel || "Send Message")} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </form>
      </div>
    </PageShell>
  );
}

export function EcomFAQ() {
  const { config: cfg } = useTemplate();
  const pc = cfg.copy?.pages?.faq || {};
  const faqs = cfg.faqs?.length ? cfg.faqs : [
    { q: "How long does shipping take?", a: "Orders ship within 2–4 business days with tracking." },
    { q: "What's your return policy?", a: "30 days from delivery for unused items in original packaging." },
    { q: "Do you ship internationally?", a: "We're rolling out international shipping — subscribe to hear first." },
  ];
  const [open, setOpen] = useState(0);
  return (
    <PageShell eyebrow={pc.eyebrow || "— FAQ"} eyebrowTestId="faq-page-eyebrow" title={pc.title || "Questions, answered."} titleTestId="faq-page-title">
      <div className="max-w-3xl divide-y divide-neutral-200 border-y border-neutral-200" data-testid="faq-section">
        {faqs.map((f, i) => (
          <div key={i}>
            <button
              className="w-full text-left py-5 flex items-center justify-between gap-6"
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-question-${i}`}
            >
              <span className="font-medium">{f.q}</span>
              <span className="text-2xl leading-none">{open === i ? "–" : "+"}</span>
            </button>
            {open === i && (
              <p className="pb-6 text-neutral-600 text-sm leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</p>
            )}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

export function EcomBestSellers() {
  const { config: cfg } = useTemplate();
  const pc = cfg.copy?.pages?.bestSellers || {};
  const accent = cfg.brand?.accentColor || "#E60000";
  const products = (cfg.demoProducts || [])
    .slice()
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, 12);
  return (
    <PageShell eyebrow={pc.eyebrow || "— Best Sellers"} eyebrowTestId="best-page-eyebrow" title={pc.title || "Best sellers."} titleTestId="best-page-title">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
        {products.map((p, i) => (
          <ProductCard key={p.id || i} product={p} accent={accent} eager={i < 4} />
        ))}
      </div>
    </PageShell>
  );
}

export function makeEcomPolicyPage(policyKey) {
  return function EcomPolicyPage() {
    const { config: cfg } = useTemplate();
    const pc = cfg.copy?.pages?.policies?.[policyKey] || {};
    const body = cfg.policyBodies?.[policyKey] || "";
    const paras = body.split(/\n+/).filter(Boolean);
    return (
      <PageShell
        eyebrow={pc.eyebrow || "— Info"}
        eyebrowTestId={`policy-eyebrow-${policyKey}`}
        title={pc.title || policyKey.charAt(0).toUpperCase() + policyKey.slice(1)}
        titleTestId={`policy-title-${policyKey}`}
      >
        <div className="max-w-3xl space-y-5 text-neutral-600 leading-relaxed" data-testid={`policy-body-${policyKey}`}>
          {paras.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </PageShell>
    );
  };
}

export const ECOM_STATIC_PAGES = {
  about: EcomAbout,
  contact: EcomContact,
  faq: EcomFAQ,
  "best-sellers": EcomBestSellers,
  shipping: makeEcomPolicyPage("shipping"),
  returns: makeEcomPolicyPage("returns"),
  privacy: makeEcomPolicyPage("privacy"),
  terms: makeEcomPolicyPage("terms"),
  support: makeEcomPolicyPage("support"),
};
